import React from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { copyToClipboard } from '../utils/clipboard';
import { toast } from '../hooks/use-toast';

type Example = {
  label: string;
  hint?: string;
  code: string;
};

const host = 'pastey.link';
const base = `https://${host}`;

function buildExamples(pageId: string): Example[] {
  const id = pageId;
  return [
    {
      label: 'Open in a browser',
      hint: 'Paste this URL into any browser to see and edit clips visually.',
      code: `${base}/${id}`,
    },
    {
      label: 'Create this clipboard from the CLI (if it doesn’t exist yet)',
      hint: 'One-time setup: reserves the name so you can post to it. Skip if you created it from the homepage.',
      code: `curl -X POST -H 'Content-Type: application/json' -d '{"id":"${id}"}' ${base}/api/pages`,
    },
    {
      label: 'List all clips (terminal)',
      hint: 'Prints the clip IDs and titles, plus usage hints.',
      code: `curl ${base}/${id}`,
    },
    {
      label: 'Get one clip — by title',
      hint: 'Prints only the clip’s content. Title is case-insensitive.',
      code: `curl ${base}/${id}/<title>`,
    },
    {
      label: 'Get one clip — by numeric id',
      hint: 'Use the id shown when you list clips.',
      code: `curl ${base}/${id}/<id>`,
    },
    {
      label: 'Post a clip from stdin',
      hint: 'Pipe anything into curl; the body becomes a new clip. Title is auto-generated.',
      code: `echo 'hello world' | curl -d @- ${base}/${id}`,
    },
    {
      label: 'Post a clip with a title',
      hint: 'Put the title in the URL path so you can find it later by name.',
      code: `echo 'hello world' | curl -d @- ${base}/${id}/MyTitle`,
    },
    {
      label: 'Post the output of a command',
      hint: 'Anything on stdin works — logs, ifconfig, git diff, etc.',
      code: `ip a | curl -d @- ${base}/${id}/network`,
    },
    {
      label: 'Get JSON (for scripts / apps)',
      hint: 'Add Accept: application/json to get structured data instead of plain text.',
      code: `curl -H 'Accept: application/json' ${base}/${id}`,
    },
  ];
}

function CodeRow({ example }: { example: Example }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = async () => {
    const ok = await copyToClipboard(example.code);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast({ title: 'Copied', description: example.label });
    } else {
      toast({
        title: 'Copy failed',
        description: 'Your browser blocked clipboard access.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium">{example.label}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          aria-label={`Copy: ${example.label}`}
          className="h-7 px-2"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
      {example.hint && (
        <p className="text-xs text-muted-foreground">{example.hint}</p>
      )}
      <pre
        className="bg-muted text-foreground px-3 py-2 rounded-md text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all"
        onClick={handleCopy}
      >
        <code>{example.code}</code>
      </pre>
    </div>
  );
}

export function UsageGuide({ pageId }: { pageId: string }) {
  const isPlaceholder = pageId === '<id>' || pageId === 'xxxxx';
  const examples = buildExamples(pageId);

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">How to use {isPlaceholder ? 'pastey' : `clipboard ${pageId}`}</h2>
        <p className="text-sm text-muted-foreground">
          A clipboard is a short URL that holds up to 10 text snippets for 48 hours. The id can be
          a random 5 characters, or a name you pick (3-32 chars; letters, digits, <code className="bg-muted px-1 rounded">-</code>, <code className="bg-muted px-1 rounded">_</code>).
          Use it from any browser, or straight from the terminal with <code className="bg-muted px-1 rounded">curl</code>.
          {isPlaceholder && ' Replace '}<code className={isPlaceholder ? 'bg-muted px-1 rounded' : 'hidden'}>{pageId}</code>{isPlaceholder && ' in each example below with your own clipboard id.'}
        </p>
        <p className="text-sm text-muted-foreground">
          Always use <code className="bg-muted px-1 rounded">https://</code> in terminal commands — plain <code className="bg-muted px-1 rounded">pastey.link</code> gets redirected and some <code className="bg-muted px-1 rounded">curl</code> versions drop the POST body on redirect.
        </p>
      </div>

      <div className="grid gap-4">
        {examples.map((ex) => (
          <CodeRow key={ex.label} example={ex} />
        ))}
      </div>

      <div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
        <p className="font-medium text-foreground">Good to know</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>Clipboards auto-expire 48 hours after creation. You can click <em>Extend 48 Hours</em> on a clipboard page to reset the timer.</li>
          <li>Max 10 clips per clipboard, 200&nbsp;KB per clip.</li>
          <li>Anyone who knows the 5-char id can read and post — treat the URL as a shared secret.</li>
          <li>The terminal examples also work on Windows (PowerShell / WSL) and macOS Terminal.</li>
        </ul>
      </div>
    </div>
  );
}
